import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Download, Image, Share2, X, AlertTriangle, Lock, Shield } from "lucide-react";
import axios from "axios";
import { API, pageVariants } from "../components/app/constants";
import { CreditUpsellModal, PremiumLoader } from "../components/shared";
import { SEO } from "../components/shared/SEO";

export const GalleryPage = ({ user, onUserUpdate, onNavigate }) => {
  const [searchParams] = useSearchParams();
  const [portraits, setPortraits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpsell, setShowUpsell] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [vaultActive, setVaultActive] = useState(false);
  const [showVaultModal, setShowVaultModal] = useState(false);
  const [vaultLoading, setVaultLoading] = useState(false);
  const [vaultSuccess, setVaultSuccess] = useState(false);
  const [shareTarget, setShareTarget] = useState(null);

  useEffect(() => {
    if (!user) return;
    const fetchPortraits = async () => {
      try {
        const response = await axios.get(`${API}/portraits/${user.id}`);
        setPortraits(response.data.portraits || []);
        setVaultActive(response.data.vault_active || false);
      } catch {
        // Failed to load — show empty state
      } finally {
        setLoading(false);
      }
    };
    fetchPortraits();
  }, [user]);

  // Handle vault success return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    const vaultParam = searchParams.get("vault");
    if (vaultParam === "success" && sessionId) {
      setVaultLoading(true);
      const pollVault = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/checkout/status/${sessionId}`);
          if (res.data.payment_status === "paid") {
            clearInterval(pollVault);
            setVaultActive(true);
            setVaultSuccess(true);
            setVaultLoading(false);
            // Clean URL
            window.history.replaceState({}, "", window.location.pathname);
          }
        } catch { /* keep polling */ }
      }, 2000);
      setTimeout(() => { clearInterval(pollVault); setVaultLoading(false); }, 60000);
      return () => clearInterval(pollVault);
    }
  }, [searchParams]);

  const handleVaultCheckout = async (plan) => {
    setVaultLoading(true);
    try {
      const origin = window.location.origin;
      const res = await axios.post(`${API}/vault/checkout`, {
        user_id: user.id,
        plan,
        origin_url: origin,
      });
      window.location.href = res.data.url;
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to start checkout");
      setVaultLoading(false);
    }
  };

  const downloadPortrait = async (portrait) => {
    try {
      const res = await axios.get(`${API}/portraits/download-hd/${portrait.id}?user_id=${user.id}`);
      const link = document.createElement("a");
      link.href = `data:image/png;base64,${res.data.image_base64}`;
      link.download = `starframe-${portrait.style}-${portrait.id}.png`;
      link.click();
      if (res.data.credits_remaining !== undefined) {
        onUserUpdate({ ...user, credits: res.data.credits_remaining });
      }
    } catch (err) {
      if (err.response?.status === 402) {
        setShowUpsell(true);
      }
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/portraits/${deleteTarget.id}?user_id=${user.id}`);
      setPortraits(prev => prev.filter(p => p.id !== deleteTarget.id));
    } catch (err) {
      alert("Failed to delete portrait");
    }
    setDeleting(false);
    setDeleteTarget(null);
  };

  if (!user) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="min-h-screen pt-32 pb-20 px-6 flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/60 mb-4">Please sign in to view your gallery</p>
          <button onClick={() => onNavigate("/start")} className="btn-primary" data-testid="gallery-signin-btn">Sign In</button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate" exit="exit" className="min-h-screen pt-32 pb-20 px-6">
      <SEO title="My Portrait Gallery" path="/gallery" description="View and download your AI-generated portraits. HD downloads, social sharing, and more. Your personal AI portrait collection." noindex />
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">Your Collection</p>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Gallery</h1>
        </div>

        {/* Vault / Storage Banner */}
        {!vaultActive && portraits.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-gradient-to-r from-gold/10 to-gold/5 border border-gold/20 rounded-xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3"
            data-testid="vault-banner"
          >
            <div className="flex items-start gap-3">
              <Lock className="w-5 h-5 text-gold mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-white font-medium text-sm">Protect your portraits with Vault</p>
                <p className="text-white/50 text-xs mt-0.5">Without Vault, portraits expire after 30 days. Subscribe to keep them safe.</p>
              </div>
            </div>
            <button 
              onClick={() => setShowVaultModal(true)}
              className="bg-[#D4AF37] text-black text-xs font-bold px-4 py-2 rounded-full hover:bg-[#D4AF37]/80 transition-colors flex-shrink-0"
              data-testid="vault-upgrade-btn"
            >
              From $4.99/mo
            </button>
          </motion.div>
        )}

        {/* Vault Active Badge */}
        {vaultActive && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="mb-6 bg-green-500/10 border border-green-500/20 rounded-xl p-3 flex items-center gap-3"
          >
            <Shield className="w-5 h-5 text-green-400 flex-shrink-0" />
            <p className="text-green-300 text-sm">Vault active — your portraits are protected while your subscription is active</p>
          </motion.div>
        )}

        {/* Vault Success Toast */}
        {vaultSuccess && (
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="mb-6 bg-green-500/15 border border-green-500/30 rounded-xl p-4 flex items-center gap-3"
          >
            <Check className="w-5 h-5 text-green-400 flex-shrink-0" />
            <div>
              <p className="text-green-300 font-medium text-sm">Vault Activated!</p>
              <p className="text-green-300/60 text-xs">Your portraits are securely stored while your vault subscription is active.</p>
            </div>
            <button onClick={() => setVaultSuccess(false)} className="ml-auto text-green-400/50 hover:text-green-400">
              <X className="w-4 h-4" />
            </button>
          </motion.div>
        )}

        {/* Expiring Soon Warning */}
        {!vaultActive && portraits.some(p => p.expires_in_days !== null && p.expires_in_days <= 7) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-6 bg-red-500/10 border border-red-500/20 rounded-xl p-3 flex items-center gap-3"
            data-testid="expiry-warning"
          >
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-red-300 text-sm">
              {portraits.filter(p => p.expires_in_days !== null && p.expires_in_days <= 7).length} portrait(s) expiring soon — download HD or upgrade to Vault to keep them
            </p>
          </motion.div>
        )}
        
        <p className="text-center text-white/30 text-xs mb-6">
          Previews shown with watermark. Download HD for clean versions.
        </p>

        {loading ? (
          <div className="flex justify-center py-20"><PremiumLoader message="Loading your gallery..." /></div>
        ) : portraits.length === 0 ? (
          <div className="text-center py-20">
            <Image className="w-16 h-16 text-white/20 mx-auto mb-6" />
            <h3 className="font-serif text-2xl mb-4">No Portraits Yet</h3>
            <p className="text-white/60 mb-8">Create your first stunning portrait!</p>
            <button onClick={() => onNavigate("/create")} className="btn-primary" data-testid="create-first-btn">Create Portrait</button>
          </div>
        ) : (
          <div className="gallery-grid">
            {portraits.map((portrait) => (
              <motion.div key={portrait.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="group relative">
                <div className="portrait-result glass-card p-2 relative">
                  <img 
                    src={`data:image/png;base64,${portrait.image_base64}`} 
                    alt={`${portrait.style} portrait`}
                    data-testid={`gallery-portrait-${portrait.id}`}
                  />
                  {/* Expiration badge */}
                  {portrait.expires_in_days !== null && portrait.expires_in_days <= 7 && (
                    <div className="absolute top-3 left-3 bg-red-500/90 px-2 py-0.5 rounded text-[10px] text-white font-semibold" data-testid={`expiry-badge-${portrait.id}`}>
                      {portrait.expires_in_days <= 0 ? "Expiring today" : `${portrait.expires_in_days}d left`}
                    </div>
                  )}
                  {/* Watermark indicator */}
                  <div className="absolute bottom-3 right-3 bg-black/60 px-2 py-0.5 rounded text-[9px] text-white/40">
                    Preview
                  </div>
                </div>
                
                {/* Hover overlay with actions */}
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/75 opacity-0 group-hover:opacity-100 transition-opacity p-4 rounded-xl">
                  <button 
                    onClick={() => downloadPortrait(portrait)}
                    className="btn-primary w-full flex items-center justify-center gap-2 text-sm"
                    data-testid={`download-${portrait.id}`}
                  >
                    <Download className="w-4 h-4" />
                    Download HD
                    {!portrait.hd_downloaded && <span className="text-[10px] opacity-70">(1 Credit)</span>}
                  </button>
                  <button
                    onClick={() => setShareTarget(portrait)}
                    className="btn-secondary w-full flex items-center justify-center gap-2 text-sm"
                    data-testid={`share-${portrait.id}`}
                  >
                    <Share2 className="w-4 h-4" />
                    Share
                  </button>
                  <button
                    onClick={() => setDeleteTarget(portrait)}
                    className="w-full flex items-center justify-center gap-2 text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 py-2 rounded-lg transition-colors"
                    data-testid={`delete-${portrait.id}`}
                  >
                    <X className="w-4 h-4" />
                    Delete
                  </button>
                </div>
                <p className="text-center mt-3 text-sm text-white/60 capitalize">{(portrait.style || "").replace(/_/g, " ")}</p>
              </motion.div>
            ))}
          </div>
        )}
      </div>
      
      {/* Delete Confirmation Modal */}
      <AnimatePresence>
        {deleteTarget && (
          <motion.div
            data-testid="delete-confirm-modal"
            className="fixed inset-0 z-[100] flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => !deleting && setDeleteTarget(null)} />
            <motion.div
              className="relative bg-[#0a0a0a] border border-red-500/30 rounded-2xl p-6 max-w-sm w-full mx-4"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
            >
              <div className="absolute top-0 left-0 right-0 h-1 bg-red-500/50 rounded-t-2xl" />
              <div className="text-center mb-5">
                <div className="w-14 h-14 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <AlertTriangle className="w-7 h-7 text-red-400" />
                </div>
                <h3 className="text-lg font-bold text-white">Delete Portrait?</h3>
                <p className="text-white/50 text-sm mt-2">This will permanently delete this portrait. This action cannot be undone.</p>
              </div>
              <div className="flex gap-3">
                <button
                  data-testid="delete-cancel"
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleting}
                  className="flex-1 bg-white/10 hover:bg-white/20 text-white py-2.5 rounded-xl text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  data-testid="delete-confirm"
                  onClick={confirmDelete}
                  disabled={deleting}
                  className="flex-1 bg-red-500 hover:bg-red-400 text-white py-2.5 rounded-xl text-sm font-bold transition-colors flex items-center justify-center gap-2"
                >
                  {deleting ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : "Delete"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Share Picker Modal */}
      <AnimatePresence>
        {shareTarget && (
          <motion.div
            data-testid="share-picker-modal"
            className="fixed inset-0 z-[100] flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setShareTarget(null)} />
            <motion.div
              className="relative bg-[#0a0a0a] border border-white/10 rounded-2xl p-6 max-w-sm w-full mx-4"
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
            >
              <div className="absolute top-0 left-0 right-0 h-1 bg-[#D4AF37]/50 rounded-t-2xl" />
              <div className="text-center mb-5">
                <Share2 className="w-8 h-8 text-[#D4AF37] mx-auto mb-2" />
                <h3 className="font-serif text-xl">Share Portrait</h3>
                <p className="text-white/50 text-xs mt-1">Choose where to share your {(shareTarget?.style || "").replace(/_/g, " ")} portrait</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: "twitter", name: "Twitter / X", color: "#1DA1F2", url: (s) => `https://twitter.com/intent/tweet?text=${encodeURIComponent("Check out my AI portrait from StarFrame AI!")}&url=${encodeURIComponent(s)}` },
                  { id: "facebook", name: "Facebook", color: "#1877F2", url: (s) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(s)}` },
                  { id: "linkedin", name: "LinkedIn", color: "#0A66C2", url: (s) => `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(s)}` },
                  { id: "whatsapp", name: "WhatsApp", color: "#25D366", url: (s) => `https://wa.me/?text=${encodeURIComponent("Check out my AI portrait from StarFrame AI! " + s)}` },
                  { id: "viber", name: "Viber", color: "#7360F2", url: (s) => `viber://forward?text=${encodeURIComponent("Check out my AI portrait from StarFrame AI! " + s)}` },
                  { id: "telegram", name: "Telegram", color: "#26A5E4", url: (s) => `https://t.me/share/url?url=${encodeURIComponent(s)}&text=${encodeURIComponent("Check out my AI portrait from StarFrame AI!")}` },
                ].map(platform => (
                  <button
                    key={platform.id}
                    onClick={() => {
                      const shareUrl = `https://starframeai.com?ref=${user?.referral_code || ""}`;
                      window.open(platform.url(shareUrl), "_blank", "width=600,height=400");
                      setShareTarget(null);
                    }}
                    className="py-3 rounded-xl text-sm font-medium transition-all hover:scale-105"
                    style={{ backgroundColor: `${platform.color}20`, color: platform.color }}
                    data-testid={`gallery-share-${platform.id}`}
                  >
                    {platform.name}
                  </button>
                ))}
              </div>
              <button
                onClick={() => {
                  const shareUrl = `https://starframeai.com?ref=${user?.referral_code || ""}`;
                  navigator.clipboard.writeText(shareUrl);
                  setShareTarget(null);
                }}
                className="mt-3 w-full py-2.5 bg-white/5 border border-white/10 rounded-xl text-sm text-white/70 hover:bg-white/10 transition-colors"
                data-testid="gallery-share-copy"
              >
                Copy Link
              </button>
              <button
                onClick={() => setShareTarget(null)}
                className="mt-2 w-full py-2 text-white/30 text-xs hover:text-white/50 transition-colors"
              >
                Cancel
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Credit Upsell Modal */}
      <CreditUpsellModal 
        isOpen={showUpsell}
        onClose={() => setShowUpsell(false)}
        onNavigate={onNavigate}
        creditsNeeded={1}
        currentCredits={user?.credits || 0}
      />

      {/* Vault Subscription Modal */}
      <AnimatePresence>
        {showVaultModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center"
          >
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => !vaultLoading && setShowVaultModal(false)} />
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              className="relative bg-[#0a0a0a] border border-gold/30 rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl"
            >
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-gold/0 via-gold to-gold/0 rounded-t-2xl" />
              <div className="text-center mb-5">
                <div className="w-14 h-14 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Shield className="w-7 h-7 text-gold" />
                </div>
                <h3 className="text-xl font-bold text-white">Vault Storage</h3>
                <p className="text-white/50 text-sm mt-1">Keep your AI portraits forever. No more 30-day expiry.</p>
              </div>
              <div className="space-y-3 mb-5">
                <button
                  data-testid="vault-monthly-btn"
                  onClick={() => handleVaultCheckout("monthly")}
                  disabled={vaultLoading}
                  className="w-full p-4 rounded-xl bg-gold/15 border-2 border-gold hover:bg-gold/25 transition-all text-left disabled:opacity-50"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-semibold text-sm">Monthly</p>
                      <p className="text-white/40 text-xs mt-0.5">Permanent storage for all portraits</p>
                    </div>
                    <span className="text-gold font-bold text-lg">$4.99</span>
                  </div>
                </button>
                <button
                  data-testid="vault-yearly-btn"
                  onClick={() => handleVaultCheckout("yearly")}
                  disabled={vaultLoading}
                  className="w-full p-4 rounded-xl bg-white/5 border-2 border-white/10 hover:bg-white/10 transition-all text-left relative overflow-hidden disabled:opacity-50"
                >
                  <span className="absolute top-2 right-2 text-[9px] bg-green-500 text-white px-1.5 py-0.5 rounded-full font-bold">SAVE 50%</span>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-white font-semibold text-sm">Yearly</p>
                      <p className="text-white/40 text-xs mt-0.5">Best value — $2.50/mo equivalent</p>
                    </div>
                    <span className="text-white font-bold text-lg">$29.99</span>
                  </div>
                </button>
              </div>
              {vaultLoading && (
                <div className="flex items-center justify-center gap-2 mb-3">
                  <div className="w-4 h-4 border-2 border-gold/30 border-t-gold rounded-full animate-spin" />
                  <span className="text-white/50 text-sm">Redirecting to checkout...</span>
                </div>
              )}
              <button onClick={() => setShowVaultModal(false)} disabled={vaultLoading}
                className="w-full text-white/40 text-xs hover:text-white/60 transition-colors py-1 disabled:opacity-50">
                Maybe later
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};


